import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Select,
  Button,
  message,
  Row,
  Col, Modal, Drawer, Divider
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useLocation, useNavigate } from "react-router-dom";
import regionWiseContentManagementApi from '../../services/regionWiseContentManagementApi';
import cityApi from '../../services/cityApi';
import poiApi from '../../services/poiApi';
import "./rearrangLlist.css"

const { Title } = Typography;
const { Option } = Select;

// Sortable Item
const SortableItem = ({ id, label, isEditMode }: { id: string; label: string; isEditMode: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: "12px 16px",
    marginBottom: 8,
    borderRadius: 6,
    background: "#f9f9f9",
    cursor: isEditMode ? "grab" : "default",
    userSelect: "none",
  };
  return (
    <div ref={setNodeRef} style={style} {...(isEditMode ? { ...attributes, ...listeners } : {})}>
      {label} {/* Show city name instead of id */}
    </div>
  );
};

const RearrangeCityPage: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [cities, setCities] = useState<CityResponse[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const [regions, setRegions] = useState<{ id: string; sectionName: string }[]>([]);


  const sensors = useSensors(useSensor(PointerSensor));
  const [showPreview, setShowPreview] = useState(false);

  const [regionName, setRegionName] = useState<string>("");
  const [regionDescription, setRegionDescription] = useState<string>("");
  const [regionFileUrl, setRegionFileUrl] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [originalCities, setOriginalCities] = useState<CityResponse[]>([]);
  const DEFAULT_IMAGE = "/images/region-02.png";
  const isReordered = () => doneClicked && changesMade;
  const [doneClicked, setDoneClicked] = useState(false);
  const [changesMade, setChangesMade] = useState(false);

  // Load regions from API
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await regionWiseContentManagementApi.getRegionWiseContentManagement(); // replace with your API

        if (Array.isArray(response.data)) {
          setRegions(response.data);
        }
      } catch (error) {
        console.error(error);
        messageApi.error("Failed to load regions");
      }
    };
    fetchRegions();
  }, []);

  // Load cities dynamically (you can replace this with API too)
  const handleRegionChange = async (regionId: string) => {
    setSelectedRegion(regionId);
    setCities([]); // reset cities while loading
    try {
      // Wait for API response

      const response = await cityApi.getCitiesByRegionId(regionId);
      // Make sure data exists
      if (response?.cityDataResponses?.length) {
        setCities(response.cityDataResponses);
        setOriginalCities(response.cityDataResponses); // <--- save original order
        setRegionName(regions.find(r => r.id === regionId)?.sectionName || "");
        setRegionDescription(response.description);
        setRegionFileUrl(response.fileUrl);

      } else {
        setCities([]);
        setOriginalCities([]); // reset original copy too
        setRegionDescription("");
        setRegionFileUrl("");
        setRegionName("");
        messageApi.warning("No cities found for the selected region");
      }
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to load cities for this region");
      setCities(response.cityDataResponses);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = cities.findIndex(city => city.id === active.id);
      const newIndex = cities.findIndex(city => city.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const newCities = arrayMove(cities, oldIndex, newIndex);
      setCities(newCities);

      // Check if order is changed compared to original
      const changed = newCities.some((city, index) => city.id !== originalCities[index]?.id);
      setChangesMade(changed);
    }
  };

  const handleSave = async () => {

    const payload = {
      regionID: selectedRegion,
      sortCityRequests: cities.map((city, index) => ({
        cityID: city.id,
        sortOrder: index, // start from 0
      })),
    };

    try {
      await poiApi.saveReorderCitys(payload) // <-- your API call
      messageApi.success("Cities rearranged successfully");
      //setEditMode(false);
    } catch (err) {
      messageApi.error("Failed to reorder cities");
    }
  };


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const showModal = () => setIsModalOpen(true);
  const handleOk = () => {
    messageApi.info("Changes discarded.");
    setCities(originalCities);   // restore original order
    setIsModalOpen(false);       // close modal

    //  reset flags so buttons are disabled
    setDoneClicked(false);
    setChangesMade(false);
  };
  const handleCancel = () => setIsModalOpen(false);

  const showsaveModal = () => {
    if (!selectedRegion) {
      messageApi.error("Please select a region before saving");
      return;
    }

    if (cities.length === 0) {
      messageApi.warning("No cities found to save for the selected region");
      return;
    }

    setIsSaveModalOpen(true);
  };
  const handleSaveOk = () => {
    handleSave();       // call API
    setIsSaveModalOpen(false); // close modal

    // ✅ reset flags so buttons are disabled
    setDoneClicked(false);
    setChangesMade(false);
  };
  const handleSaveCancel = () => setIsSaveModalOpen(false);

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Row gutter={[16, 16]}>
        {/* Left Section */}
        <Col xs={24} lg={16}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            <Title level={3} style={{ color: "#41787C", marginBottom: 0 }}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined style={{ fontSize: 18, color: "#41787C", marginRight: 10 }} />}
                style={{ marginBottom: "10px", padding: 0, color: '#41787C' }}
                onClick={() => navigate(-1)}
              />
              Rearrange City
            </Title>
            <div style={{ marginLeft: "auto" }}>
              <Button
                icon={isEditMode ? <CheckOutlined /> : <EditOutlined />}
                onClick={() => {
                  if (isEditMode) {
                    //  When user clicks DONE
                    const changed = cities.some((city, index) => city.id !== originalCities[index]?.id);
                    setChangesMade(changed);      // track reorder state
                    setDoneClicked(true);         // mark done clicked
                    setIsEditMode(false);         // exit edit mode
                  } else {
                    // When user clicks EDIT again
                    setIsEditMode(true);          // enter edit mode
                    setDoneClicked(false);        // disable save/discard until next Done
                    setChangesMade(false);        // reset change tracker
                    setOriginalCities([...cities]); // keep new base order
                  }
                }}
                disabled={cities.length === 0}
              >
                {isEditMode ? "Done" : "Edit"}
              </Button>
            </div>
          </div>

          {/* Region Dropdown */}
          <div style={{ marginBottom: 20 }}>
            <Title level={5}>Select Region<span style={{ color: "red" }}>*</span></Title>

            <Select
              value={selectedRegion || ""}
              style={{ width: "100%", maxWidth: 400 }}
              onChange={(value: string) => {
                setSelectedRegion(value);
                if (!value) {
                  // Clear cities if default option selected
                  setCities([]);
                  setRegionDescription("");
                  setRegionFileUrl("");
                  setRegionName("");
                } else {
                  handleRegionChange(value);
                }
              }}
            >
              {/* Default option at first position */}
              <Option value="">Select a region</Option>

              {regions.map((r) => (
                <Option key={r.id} value={r.id}>
                  {r.sectionName}
                </Option>
              ))}
            </Select>
          </div>

          {/* City List */}

          <div>
            <Title level={5}>List of Cities</Title>
            {cities.length > 0 ? (
              isEditMode ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={cities.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    {cities.map((city) => (
                      <SortableItem key={city.id} id={city.id} label={city.cityName} isEditMode={isEditMode} />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                cities.map((city) => (
                  <div
                    key={city.id}
                    style={{
                      padding: "12px 16px",
                      marginBottom: 8,
                      borderRadius: 6,
                      background: "#f9f9f9",
                    }}
                  >
                    {city.cityName} {/* Show city name */}
                  </div>
                ))
              )
            ) : (
              <p style={{ color: "#888" }}>No cities found for the selected region.</p>
            )}
          </div>


        </Col>

        {/* Right Section - Info Panel */}
        <Col xs={24} lg={8}>
          <Card style={{ textAlign: "center", borderRadius: 12 }}>
            <img
              src={regionFileUrl || DEFAULT_IMAGE}//src="./images/MaskGroup2@2x.png"
              alt="region"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 8,
                marginBottom: 16,
                maxHeight: 400,
                objectFit: "cover",
              }}
            />
            <Title level={5}>{regionName}</Title>
            <p style={{ color: "#555" }}>
              {regionDescription}
            </p>

            {/* Footer Buttons */}
            <div
              style={{
                marginTop: 30,
                display: "flex",
                justifyContent: "center", // centers the whole row
                alignItems: "center",
                gap: 16, // equal space between buttons
                flexWrap: "nowrap",
              }}
              className="responsive-buttons"
            >
              <Button
                onClick={() => setShowPreview(true)}
                disabled={cities.length === 0}
              >
                Mobile Preview
              </Button>
              <Button
                danger
                onClick={showModal}
                disabled={!isReordered()}
              >
                Discard
              </Button>

              <Modal
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={null} // custom footer
                centered
                closable={false}   // hides the default X close button
                mask={false}       // disables dark overlay
                maskClosable={false} // clicking outside does NOT close modal
                bodyStyle={{
                  textAlign: "center",
                  padding: "24px 16px",
                  borderRadius: 12,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Discard Changes
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                  Are you sure you want to discard changes?
                </p>

                {/* Custom Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      backgroundColor: "#41787C",
                      borderColor: "#41787C",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleOk}
                  >
                    Yes
                  </Button>

                  <Button
                    block
                    style={{
                      backgroundColor: "#f2f2f2",
                      border: "none",
                      color: "#333",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Modal>

              <Button
                type="primary"
                onClick={showsaveModal}
                disabled={!isReordered()}
              >
                Save
              </Button>

              <Modal
                open={isSaveModalOpen}
                onOk={handleSaveOk}
                onCancel={handleSaveCancel}
                footer={null} // custom footer
                centered
                closable={false}
                bodyStyle={{
                  textAlign: "center",
                  padding: "24px 16px",
                  borderRadius: 12,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Save Changes
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                  Are you sure you want to save the changes?
                </p>

                {/* Custom Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      backgroundColor: "#41787C",
                      borderColor: "#41787C",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleSaveOk} // call your save function here
                  >
                    Yes, Save
                  </Button>


                  <Button
                    block
                    style={{
                      backgroundColor: "#f2f2f2",
                      border: "none",
                      color: "#333",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleSaveCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Modal>
            </div>
          </Card>
        </Col>

      </Row>


      <Drawer
        open={showPreview}
        onClose={() => setShowPreview(false)}
        placement="right"
        width={420} // Adjust width for mobile preview
        closable={false}
        bodyStyle={{ padding: 0, display: "flex", justifyContent: "center" }}
      >
        <div
          style={{
            width: 360,
            height: 640,
            border: "16px solid black",
            borderRadius: "30px",
            padding: "0",
            position: "relative",
            background: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 12,
              borderBottom: "1px solid #ddd",
              fontWeight: "bold",
              fontSize: 16,
              color: "#a0522d",
            }}
          >
            {/* Back Arrow */}
            <ArrowLeftOutlined style={{ fontSize: 18, cursor: "pointer" }} />

            {/* Title */}
            <div style={{ flex: 1, textAlign: "center", marginLeft: -18 }}>
              Regional Section
            </div>

            {/* Empty space for balance */}
            <div style={{ width: 18 }} />
          </div>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {/* Region card */}
            <div style={{ background: "#fff", padding: 12 }}>
              <h3 style={{ marginBottom: "-16px" }}>{regionName}</h3>
              <Divider style={{ borderColor: "#a0522d", borderWidth: 1 }} />
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "#666" }}>
                {regionDescription || "No description available."}
              </p>

              {/* Map placeholder */}
              <div
                style={{
                  height: 160,
                  borderRadius: 8,
                  overflow: "hidden",
                  marginBottom: 12,
                  background: "#eee",
                }}
              >
                {/* Replace with actual map image or component */}
                <img
                  src={"/images/ra-mapbox.jpg"}
                  alt="map"
                  style={{ width: "100%", height: "100%" }}
                />
              </div>

              {/* Route heading */}
              <h4 style={{ fontSize: 14, margin: "0 0 8px" }}>Route</h4>

              {/* Cities list */}
              <div>
                {cities.map((city, index) => (

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    {/* Left side: image + name + distance */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <img
                        src={city.fileUrl || "https://via.placeholder.com/20"}
                        alt={city.cityName}
                        style={{ width: 20, height: 20, objectFit: "cover", borderRadius: '50%' }}
                      />
                      <div style={{ fontWeight: 500 }}>{city.cityName}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {city.mile || "0"} mile
                      </div>
                    </div>

                    {/* Right side: population + View More */}
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {city.population || "N/A"}
                      </div>
                      <a style={{ fontSize: 12, color: "#1890ff" }}>View More</a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer button */}
          <Button
            block
            type="primary"
            style={{
              borderRadius: 0,
            }}
            onClick={() => setShowPreview(false)}
          >
            Close Preview
          </Button>
        </div>
      </Drawer>
    </div>

  );
};

export default RearrangeCityPage;


